import type { CustomFieldValues } from './types';
import moment from 'moment';
import { formatUserDate } from 'components/utils';

export type { CustomFieldValue, CustomFieldValues } from './types';

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const CUSTOM_FIELD_DATE_FORMATS = ['MM-DD-YYYY', 'M-D-YYYY', 'MM/DD/YYYY', 'M/D/YYYY'];

export const parseCustomFieldDateValue = (value: unknown): moment.Moment | null => {
  if (moment.isMoment(value)) {
    return value;
  }

  if (value instanceof Date || typeof value === 'number') {
    return moment(value);
  }

  if (typeof value !== 'string') {
    return null;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return null;
  }

  const isoDate = moment(trimmedValue, moment.ISO_8601, true);

  if (isoDate.isValid()) {
    return isoDate;
  }

  const customFieldDate = moment(trimmedValue, CUSTOM_FIELD_DATE_FORMATS, true);

  return customFieldDate.isValid() ? customFieldDate : null;
};

const formatSingleCustomFieldValue = (value: unknown, fieldType?: string): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (isObjectRecord(value) && 'value' in value) {
    return formatSingleCustomFieldValue(value.value, fieldType);
  }

  if (Array.isArray(value)) {
    return value.map(item => formatSingleCustomFieldValue(item, fieldType)).filter(Boolean).join(', ');
  }

  if (fieldType === 'date') {
    const dateValue = parseCustomFieldDateValue(value);

    return dateValue ? formatUserDate(dateValue) : String(value);
  }

  return String(value);
};

export const formatCustomFieldValue = (customFieldValue: unknown, fieldType?: string): string =>
  formatSingleCustomFieldValue(customFieldValue, fieldType);

export const readCustomFieldInputValue = (stored: unknown, fieldType?: string): unknown => {
  const value = isObjectRecord(stored) && 'value' in stored ? stored.value : stored;

  if (fieldType === 'multi-choice') {
    return Array.isArray(value) ? value : [];
  }

  if (fieldType === 'checkbox') {
    return value === 'yes' || value === 'no' ? value : '';
  }

  if (value === undefined || value === null) {
    return '';
  }

  return value;
};

export const normalizeCustomFieldValues = (values: unknown): CustomFieldValues => {
  const normalizedValues: CustomFieldValues = {};

  if (typeof values !== 'object' || values === null) {
    return normalizedValues;
  }

  for (const [key, value] of Object.entries(values)) {
    if (typeof value !== 'object' || value === null || !('label' in value)) {
      // Backward-compatible: older custom field values might be stored as primitive values
      // rather than the newer `{ value, label }` object shape.
      if (value === null || value === undefined) {
        continue;
      }

      normalizedValues[key] = {
        value,
        label: '',
      };
      continue;
    }

    const label = typeof value.label === 'string' ? value.label : '';
    const normalizedValue = 'value' in value ? value.value : undefined;

    normalizedValues[key] = {
      value: normalizedValue,
      label,
    };
  }

  return normalizedValues;
};
